if (!import.meta.env.VITE_AUTH_GATEWAY_URL) {
  throw new Error("VITE_AUTH_GATEWAY_URL is not configured");
}

const AUTH_GATEWAY_URL = import.meta.env.VITE_AUTH_GATEWAY_URL as string;

// Keep the browser refresh margin in sync with the server-side TOKEN_REFRESH_BUFFER_SECONDS
// (300 s).  By re-fetching the firebase token when less than 5 minutes of life remain, the
// browser will hit /api/auth/firebase-token exactly when the server's isAuthenticated
// middleware has already initiated (or will immediately initiate) a proactive OIDC refresh,
// so we never hand the audio gateway a token that is about to expire.
// The on-401 retry in audioApiFetch remains as a safety net for any narrow timing windows.
const REFRESH_BUFFER_MS = 300_000;

let firebaseTokenCache: { idToken: string; expiresAt: number } | null = null;
let audioSessionCache: { sessionToken: string; expiresAt: number } | null = null;

export async function getFirebaseIdToken(): Promise<string> {
  const now = Date.now();
  if (firebaseTokenCache && firebaseTokenCache.expiresAt * 1000 - now > REFRESH_BUFFER_MS) {
    return firebaseTokenCache.idToken;
  }
  const res = await fetch("/api/auth/firebase-token", { credentials: "include" });
  if (!res.ok) {
    // Preserve the server-supplied `stage` (e.g. "id-token-expired") so the UI can
    // display the right recoverable message rather than a generic upload error.
    let serverStage = "firebase-token";
    let serverMessage = `Failed to get Firebase token: ${res.status}`;
    try {
      const body = await res.clone().json();
      if (typeof body?.stage === "string") serverStage = body.stage;
      if (typeof body?.message === "string") serverMessage = body.message;
    } catch {
      // ignore parse errors — fall back to defaults above
    }
    throw Object.assign(new Error(serverMessage), {
      stage: serverStage,
      status: res.status,
    });
  }
  const data: { idToken: string; expiresAt: number } = await res.json();
  firebaseTokenCache = data;
  return data.idToken;
}

function parseExpiresAt(raw: unknown): number {
  if (typeof raw === "number") return raw;
  if (typeof raw === "string") {
    const ts = Date.parse(raw);
    if (!isNaN(ts)) return Math.floor(ts / 1000);
  }
  return Math.floor(Date.now() / 1000) + 3600;
}

async function getAudioSessionToken(): Promise<string> {
  const now = Date.now();
  if (audioSessionCache && audioSessionCache.expiresAt * 1000 - now > REFRESH_BUFFER_MS) {
    return audioSessionCache.sessionToken;
  }

  const idToken = await getFirebaseIdToken();

  let res: Response;
  try {
    res = await fetch(`${AUTH_GATEWAY_URL}/api/auth/audio-session`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    });
  } catch {
    throw Object.assign(new Error("Couldn't reach the auth gateway"), {
      stage: "auth-gateway-network",
    });
  }

  if (!res.ok) {
    throw Object.assign(new Error(`Auth gateway returned ${res.status}`), {
      stage: "auth-gateway-http",
      status: res.status,
    });
  }
  const raw = await res.json();
  const data = { sessionToken: raw.sessionToken as string, expiresAt: parseExpiresAt(raw.expiresAt) };
  audioSessionCache = data;
  return data.sessionToken;
}

async function buildHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
  const sessionToken = await getAudioSessionToken();
  return {
    "Authorization": `Bearer ${sessionToken}`,
    ...extra,
  };
}

async function audioApiFetch(url: string, init: RequestInit): Promise<Response> {
  const headers = await buildHeaders(init.headers as Record<string, string>);
  let res = await fetch(url, { ...init, headers });
  if (res.status === 401) {
    audioSessionCache = null;
    firebaseTokenCache = null;
    const retryHeaders = await buildHeaders(init.headers as Record<string, string>);
    res = await fetch(url, { ...init, headers: retryHeaders });
  }
  return res;
}

export async function createGatewaySession(
  name: string,
): Promise<{ id: string }> {
  let res: Response;
  try {
    res = await audioApiFetch(`${AUTH_GATEWAY_URL}/api/sessions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
  } catch (err: any) {
    if (err?.stage) throw err;
    throw Object.assign(new Error("Couldn't reach the audio service"), {
      stage: "audio-api-network",
    });
  }
  if (!res.ok) {
    throw Object.assign(new Error(`Audio service returned ${res.status}`), {
      stage: "audio-api-http",
      status: res.status,
    });
  }
  return res.json();
}

export async function startRecordingSession(
  gatewaySessionId: string,
  mimeType: string,
): Promise<{ id?: string; recordingId?: string; status?: string }> {
  let res: Response;
  try {
    res = await audioApiFetch(`${AUTH_GATEWAY_URL}/api/recordings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId: gatewaySessionId, mimeType }),
    });
  } catch (err: any) {
    if (err?.stage) throw err;
    throw Object.assign(new Error("Couldn't reach the audio service"), {
      stage: "audio-api-network",
    });
  }
  if (!res.ok && res.status !== 409) {
    throw Object.assign(new Error(`Audio service returned ${res.status}`), {
      stage: "audio-api-http",
      status: res.status,
    });
  }
  return res.json().catch(() => ({}));
}

export async function uploadChunk(
  recordingId: string,
  chunkIndex: number,
  blob: Blob,
): Promise<void> {
  const arrayBuffer = await blob.arrayBuffer();
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt > 0) await new Promise<void>((r) => setTimeout(r, 1000 * attempt));
    let res: Response;
    try {
      res = await audioApiFetch(`${AUTH_GATEWAY_URL}/api/recordings/${recordingId}/chunks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/octet-stream",
          "X-Chunk-Index": String(chunkIndex),
        },
        body: arrayBuffer,
      });
    } catch (err: any) {
      if (err?.stage) throw err;
      throw Object.assign(new Error("Couldn't reach the audio service"), {
        stage: "audio-api-network",
      });
    }
    if (res.ok) return;
    lastError = Object.assign(
      new Error(`Chunk upload failed: ${res.status}`),
      { status: res.status, stage: "audio-api-http" },
    );
  }
  throw lastError!;
}

export async function completeRecording(
  recordingId: string,
  totalChunks: number,
): Promise<{ audioUrl?: string; url?: string; s3Url?: string }> {
  const res = await audioApiFetch(`${AUTH_GATEWAY_URL}/api/recordings/${recordingId}/complete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ totalChunks }),
  });
  if (!res.ok) {
    throw Object.assign(new Error(`Failed to complete recording: ${res.status}`), { status: res.status });
  }
  return res.json();
}

export async function getRecordingStatus(
  recordingId: string,
): Promise<{ totalChunks?: number; chunksReceived?: number; status?: string; audioUrl?: string }> {
  const res = await audioApiFetch(`${AUTH_GATEWAY_URL}/api/recordings/${recordingId}`, {
    method: "GET",
  });
  if (!res.ok) {
    throw Object.assign(new Error(`Failed to get recording status: ${res.status}`), { status: res.status });
  }
  return res.json();
}

export async function deleteRecording(recordingId: string): Promise<void> {
  const res = await audioApiFetch(`${AUTH_GATEWAY_URL}/api/recordings/${recordingId}`, {
    method: "DELETE",
  });
  if (!res.ok && res.status !== 404) {
    throw Object.assign(new Error(`Failed to delete recording: ${res.status}`), { status: res.status });
  }
}

export function invalidateAudioSessionCache(): void {
  audioSessionCache = null;
  firebaseTokenCache = null;
}


export async function getSessionTranscript(
  gatewaySessionId: string,
): Promise<{ transcript?: string } | null> {
  const res = await audioApiFetch(`${AUTH_GATEWAY_URL}/api/sessions/${gatewaySessionId}/transcript`, {
    method: "GET",
  });
  if (res.status === 204) return null;
  if (!res.ok) {
    throw Object.assign(
      new Error(`Failed to get session transcript: ${res.status}`),
      { status: res.status },
    );
  }
  const data = await res.json().catch(() => null);
  if (!data) return null;
  const transcript = data.transcript ?? data.transcription ?? data.text;
  return { transcript };
}
